/**
 * Schema Repository
 *
 * Manages workflow schema definitions, validation rules, and schema versioning
 * FERP SAFETY: Critical for ensuring data integrity and validation consistency
 */

import { BaseRepository } from './BaseRepository';
import { Schema, ValidationRule, SchemaExample } from '../types/WorkflowEntities';

class SchemaRepository extends BaseRepository<Schema> {
    protected entityName = 'schema';
    protected graphQLType = 'Schema';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Schema | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Schema',
            criticalityLevel: 'HIGH', // Schemas are critical for data validation
            metadata: { id, schemaRepository: true }
        });

        const query = `
            query GetSchemaByTenantAndId($id: String!, $tenant: String!) {
                schema(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ schema: Schema | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 10 * 60 * 1000 // 10 minutes cache for schemas
            });

            const schema = result.schema || null;

            // ✅ FERP SAFETY: Log successful operation with schema details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Schema',
                    entityId: id,
                    found: !!schema,
                    schemaType: schema?.type,
                    schemaVersion: schema?.version,
                    schemaStatus: schema?.status,
                    timestamp: Date.now()
                });
            }

            return schema;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Schema',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(schemaId: string): Promise<Schema | null> {
        return this.findByTenantAndId(this.tenant, schemaId);
    }

    // Schema-specific methods

    /**
     * Get schema by name and version
     * FERP SAFETY: Ensures correct schema version is used
     */
    async getSchema(name: string, version?: string): Promise<Schema | null> {
        await this.validateRepositorySafety({
            operation: 'GET_SCHEMA',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Schema',
            criticalityLevel: 'HIGH',
            metadata: { name, version }
        });

        const query = `
            query GetSchemaByNameAndVersion(
                $name: String!,
                $version: String,
                $tenant: String!
            ) {
                schemaByName(name: $name, version: $version, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            name,
            version,
            tenant: this.tenant
        });

        return result.schemaByName || null;
    }

    /**
     * Get all active schemas
     * FERP SAFETY: Only returns production-ready schemas
     */
    async getActiveSchemas(type?: Schema['type']): Promise<Schema[]> {
        const query = `
            query GetActiveSchemas($type: String, $tenant: String!) {
                activeSchemas(type: $type, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            type,
            tenant: this.tenant
        });

        return result.activeSchemas || [];
    }

    /**
     * Get schema versions
     * FERP SAFETY: Track schema evolution
     */
    async getSchemaVersions(name: string): Promise<Schema[]> {
        const query = `
            query GetSchemaVersions($name: String!, $tenant: String!) {
                schemaVersions(name: $name, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            name,
            tenant: this.tenant
        });

        return result.schemaVersions || [];
    }

    /**
     * Validate data against schema
     * FERP SAFETY: Pre-validation before data submission
     */
    async validateSchema(schemaId: string, data: any): Promise<{
        valid: boolean;
        errors: Array<{
            field: string;
            message: string;
            severity: 'error' | 'warning';
        }>;
        warnings: Array<{
            field: string;
            message: string;
        }>;
    }> {
        await this.validateRepositorySafety({
            operation: 'VALIDATE_SCHEMA',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Schema',
            criticalityLevel: 'HIGH',
            metadata: { schemaId, dataProvided: !!data }
        });

        const query = `
            query ValidateDataAgainstSchema(
                $schemaId: ID!,
                $data: JSON!,
                $tenant: String!
            ) {
                validateData(schemaId: $schemaId, data: $data, tenant: $tenant) {
                    valid
                    errors {
                        field
                        message
                        severity
                    }
                    warnings {
                        field
                        message
                    }
                }
            }
        `;

        const result = await this.executeQuery(query, {
            schemaId,
            data,
            tenant: this.tenant
        });

        return result.validateData || {
            valid: false,
            errors: [{ field: '', message: 'Validation service unavailable', severity: 'error' }],
            warnings: []
        };
    }

    /**
     * Create new schema
     * FERP SAFETY: Validates schema definition before creation
     */
    async createSchema(input: {
        name: string;
        description: string;
        version: string;
        type: Schema['type'];
        content: any;
        tags?: string[];
        validationRules?: ValidationRule[];
        examples?: SchemaExample[];
    }): Promise<Schema> {
        await this.validateRepositorySafety({
            operation: 'CREATE_SCHEMA',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Schema',
            criticalityLevel: 'HIGH',
            metadata: { input }
        });

        const mutation = `
            mutation CreateSchema($input: CreateSchemaInput!) {
                createSchema(input: $input) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            input: {
                ...input,
                tenant: this.tenant
            }
        });

        return result.createSchema;
    }

    /**
     * Update schema
     * FERP SAFETY: Version-controlled updates
     */
    async updateSchema(schemaId: string, updates: Partial<{
        description: string;
        content: any;
        tags: string[];
        status: Schema['status'];
        validationRules: ValidationRule[];
        examples: SchemaExample[];
    }>): Promise<Schema> {
        await this.validateRepositorySafety({
            operation: 'UPDATE_SCHEMA',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Schema',
            criticalityLevel: 'HIGH',
            metadata: { schemaId, updates }
        });

        const mutation = `
            mutation UpdateSchema($schemaId: ID!, $updates: UpdateSchemaInput!) {
                updateSchema(schemaId: $schemaId, updates: $updates) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            schemaId,
            updates: {
                ...updates,
                tenant: this.tenant
            }
        });

        return result.updateSchema;
    }

    /**
     * Deprecate schema
     * FERP SAFETY: Graceful schema retirement
     */
    async deprecateSchema(schemaId: string): Promise<Schema> {
        await this.validateRepositorySafety({
            operation: 'DEPRECATE_SCHEMA',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Schema',
            criticalityLevel: 'HIGH',
            metadata: { schemaId }
        });

        const mutation = `
            mutation DeprecateSchema($schemaId: ID!, $tenant: String!) {
                deprecateSchema(schemaId: $schemaId, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            schemaId,
            tenant: this.tenant
        });

        return result.deprecateSchema;
    }

    /**
     * Get schemas by tags
     * FERP SAFETY: Organize schemas by domain/category
     */
    async getSchemasByTags(tags: string[]): Promise<Schema[]> {
        const query = `
            query GetSchemasByTags($tags: [String!]!, $tenant: String!) {
                schemasByTags(tags: $tags, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            tags,
            tenant: this.tenant
        });

        return result.schemasByTags || [];
    }

    /**
     * Test schema with example data
     * FERP SAFETY: Validate schema definition correctness
     */
    async testSchema(schemaId: string, exampleData: any): Promise<{
        success: boolean;
        validationResult: {
            valid: boolean;
            errors: Array<{ field: string; message: string; severity: string }>;
            warnings: Array<{ field: string; message: string }>;
        };
        performanceMs?: number;
    }> {
        const query = `
            query TestSchema(
                $schemaId: ID!,
                $exampleData: JSON!,
                $tenant: String!
            ) {
                testSchema(schemaId: $schemaId, exampleData: $exampleData, tenant: $tenant) {
                    success
                    validationResult {
                        valid
                        errors {
                            field
                            message
                            severity
                        }
                        warnings {
                            field
                            message
                        }
                    }
                    performanceMs
                }
            }
        `;

        const result = await this.executeQuery(query, {
            schemaId,
            exampleData,
            tenant: this.tenant
        });

        return result.testSchema || {
            success: false,
            validationResult: {
                valid: false,
                errors: [{ field: '', message: 'Test service unavailable', severity: 'error' }],
                warnings: []
            }
        };
    }

    /**
     * Get schema statistics
     * FERP SAFETY: Monitor schema usage and validation performance
     */
    async getSchemaStatistics(schemaId: string): Promise<{
        validationCount: number;
        successRate: number;
        averagePerformanceMs: number;
        lastValidated?: string;
        commonErrors: Array<{
            field: string;
            message: string;
            count: number;
        }>;
    }> {
        const query = `
            query GetSchemaStatistics($schemaId: ID!, $tenant: String!) {
                schemaStatistics(schemaId: $schemaId, tenant: $tenant) {
                    validationCount
                    successRate
                    averagePerformanceMs
                    lastValidated
                    commonErrors {
                        field
                        message
                        count
                    }
                }
            }
        `;

        const result = await this.executeQuery(query, {
            schemaId,
            tenant: this.tenant
        });

        return result.schemaStatistics || {
            validationCount: 0,
            successRate: 0,
            averagePerformanceMs: 0,
            commonErrors: []
        };
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            description
            version
            type
            content
            tags
            status
            validationRules {
                field
                rule
                message
                severity
            }
            examples {
                name
                description
                data
                valid
            }
            createdAt
            updatedAt
            createdBy
        `;
    }
}

export const schemaRepository = new SchemaRepository();
export { SchemaRepository };
